"use client";

import { type ReactNode } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
);

// ─── Unified brand palette ────────────────────────────────────────────────────
const BRAND = {
  primary: "rgba(99, 102, 241, 0.82)",
  primaryLine: "rgb(99, 102, 241)",
  primaryFill: "rgba(99, 102, 241, 0.08)",
  secondary: "rgba(79, 70, 229, 0.65)",
  slate: "rgba(51, 65, 85, 0.72)",
  light: "rgba(99, 102, 241, 0.48)",
  donut: [
    "rgba(99, 102, 241, 0.85)",
    "rgba(139, 92, 246, 0.75)",
    "rgba(51, 65, 85, 0.75)",
    "rgba(148, 163, 184, 0.72)",
  ],
};

function formatPlanLabel(plan: string): string {
  const map: Record<string, string> = {
    FREE: "Free",
    PRO: "Pro",
    ENTERPRISE: "Enterprise",
    STARTER: "Starter",
  };
  return map[plan] ?? plan;
}

const baseScales = {
  x: {
    grid: { display: false },
    ticks: { color: "#94a3b8", font: { size: 11 } },
    border: { display: false },
  },
  y: {
    grid: { color: "rgba(226, 232, 240, 0.7)", drawBorder: false },
    ticks: { color: "#94a3b8", font: { size: 11 }, padding: 6 },
    border: { display: false, dash: [4, 4] },
    beginAtZero: true,
  },
};

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#0f172a",
      padding: 10,
      cornerRadius: 8,
      titleFont: { size: 12, weight: "bold" as const },
      bodyFont: { size: 12 },
      borderColor: "rgba(255,255,255,0.08)",
      borderWidth: 1,
    },
  },
  scales: baseScales,
};

const doughOpts = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "64%",
  plugins: {
    legend: {
      position: "bottom" as const,
      labels: {
        font: { size: 11 },
        color: "#64748b",
        padding: 12,
        boxWidth: 10,
        boxHeight: 10,
      },
    },
    tooltip: {
      backgroundColor: "#0f172a",
      padding: 10,
      cornerRadius: 8,
      titleFont: { size: 12, weight: "bold" as const },
      bodyFont: { size: 12 },
    },
  },
};

export function ChartCard({ title, children, fullWidth = false }: { title: string; children: ReactNode; fullWidth?: boolean }) {
  return (
    <div
      className={cx(
        cf.dashSurface,
        css({
          p: "5",
          minH: "300px",
          display: "flex",
          flexDir: "column",
        }),
        fullWidth ? css({ gridColumn: { lg: "1 / -1" } }) : "",
      )}
    >
      <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "4" })}>{title}</h2>
      {children}
    </div>
  );
}

export function EmptyChart({ label }: { label: string }) {
  return (
    <div
      className={css({
        flex: "1",
        rounded: "lg",
        borderWidth: "1px",
        borderStyle: "dashed",
        borderColor: "#e2e8f0",
        bg: "#f8fafc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: "6",
        minH: "200px",
      })}
    >
      <p className={css({ fontSize: "sm", color: "#94a3b8" })}>{label}</p>
    </div>
  );
}

export function AdminCharts(props: {
  userGrowthLabels: string[];
  userGrowthData: number[];
  categoryLabels: string[];
  categoryData: number[];
  revenueLabels: string[];
  revenueData: number[];
  planLabels: string[];
  planData: number[];
  topProLabels: string[];
  topProData: number[];
  activityLabels: string[];
  activityData: number[];
}) {
  const growthEmpty = props.userGrowthData.every((v) => v === 0);
  const categoryEmpty = props.categoryData.every((v) => v === 0);
  const revenueEmpty = props.revenueData.every((v) => v === 0);
  const planEmpty = props.planData.every((v) => v === 0);
  const topEmpty = props.topProData.every((v) => v === 0);
  const actEmpty = props.activityData.every((v) => v === 0);

  const hBarTop = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: commonOptions.plugins.tooltip,
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { color: "#94a3b8", font: { size: 11 }, padding: 4 },
        grid: { color: "rgba(226,232,240,0.7)" },
        border: { display: false },
      },
      y: {
        ticks: { color: "#64748b", font: { size: 11 } },
        grid: { display: false },
        border: { display: false },
      },
    },
  };

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "4", mb: "6" })}>
      {/* Row 1: Growth + Category */}
      <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", lg: "repeat(2, 1fr)" }, gap: "4" })}>
        <ChartCard title="Crecimiento de cuentas (altas por mes)">
          {growthEmpty ? (
            <EmptyChart label="Aún no hay suficientes datos de crecimiento." />
          ) : (
            <div className={css({ h: "220px", flex: "1" })}>
              <Line
                data={{
                  labels: props.userGrowthLabels,
                  datasets: [
                    {
                      label: "Altas",
                      data: props.userGrowthData,
                      borderColor: BRAND.primaryLine,
                      borderWidth: 2.5,
                      pointRadius: 4,
                      pointBackgroundColor: BRAND.primaryLine,
                      pointBorderColor: "white",
                      pointBorderWidth: 2,
                      fill: true,
                      backgroundColor: BRAND.primaryFill,
                      tension: 0.35,
                    },
                  ],
                }}
                options={commonOptions}
              />
            </div>
          )}
        </ChartCard>

        <ChartCard title="Turnos por categoría">
          {categoryEmpty ? (
            <EmptyChart label="Aún no hay turnos clasificados por categoría." />
          ) : (
            <div className={css({ h: "220px", flex: "1" })}>
              <Bar
                data={{
                  labels: props.categoryLabels,
                  datasets: [
                    {
                      label: "Turnos",
                      data: props.categoryData,
                      backgroundColor: BRAND.primary,
                      borderRadius: 6,
                      maxBarThickness: 44,
                    },
                  ],
                }}
                options={commonOptions}
              />
            </div>
          )}
        </ChartCard>
      </div>

      {/* Row 2: Plan donut + Top pros */}
      <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", lg: "repeat(2, 1fr)" }, gap: "4" })}>
        <ChartCard title="Distribución de planes">
          {planEmpty ? (
            <EmptyChart label="Sin datos de planes." />
          ) : (
            <div className={css({ h: "220px", flex: "1" })}>
              <Doughnut
                data={{
                  labels: props.planLabels.map(formatPlanLabel),
                  datasets: [
                    {
                      data: props.planData,
                      backgroundColor: BRAND.donut,
                      borderWidth: 0,
                      hoverOffset: 6,
                    },
                  ],
                }}
                options={doughOpts}
              />
            </div>
          )}
        </ChartCard>

        <ChartCard title="Top cuentas por volumen de turnos">
          {topEmpty ? (
            <EmptyChart label="Sin turnos aún." />
          ) : (
            <div className={css({ h: "220px", flex: "1" })}>
              <Bar
                data={{
                  labels: props.topProLabels,
                  datasets: [
                    {
                      data: props.topProData,
                      backgroundColor: BRAND.slate,
                      borderRadius: 6,
                      maxBarThickness: 32,
                    },
                  ],
                }}
                options={hBarTop}
              />
            </div>
          )}
        </ChartCard>
      </div>

      {/* Row 3: Activity (full width) */}
      <ChartCard title="Actividad: turnos creados por día (14 días)" fullWidth>
        {actEmpty ? (
          <EmptyChart label="Sin actividad reciente." />
        ) : (
          <div className={css({ h: "180px", flex: "1" })}>
            <Bar
              data={{
                labels: props.activityLabels,
                datasets: [
                  {
                    label: "Turnos",
                    data: props.activityData,
                    backgroundColor: BRAND.light,
                    hoverBackgroundColor: BRAND.primary,
                    borderRadius: 5,
                    maxBarThickness: 36,
                  },
                ],
              }}
              options={commonOptions}
            />
          </div>
        )}
      </ChartCard>

      {/* Row 4: Revenue (full width) */}
      <ChartCard title="Ingresos por turnos pagados — estimado mensual (6 meses)" fullWidth>
        {revenueEmpty ? (
          <EmptyChart label="Aún no hay ingresos por pagos confirmados registrados." />
        ) : (
          <div className={css({ h: "180px", flex: "1" })}>
            <Line
              data={{
                labels: props.revenueLabels,
                datasets: [
                  {
                    label: "$",
                    data: props.revenueData,
                    borderColor: BRAND.primaryLine,
                    borderWidth: 2.5,
                    fill: true,
                    backgroundColor: BRAND.primaryFill,
                    pointRadius: 4,
                    pointBackgroundColor: BRAND.primaryLine,
                    pointBorderColor: "white",
                    pointBorderWidth: 2,
                    tension: 0.35,
                  },
                ],
              }}
              options={{
                ...commonOptions,
                plugins: {
                  ...commonOptions.plugins,
                  tooltip: {
                    ...commonOptions.plugins.tooltip,
                    callbacks: {
                      label: (ctx) => ` $${(ctx.parsed.y as number).toLocaleString("es-AR")}`,
                    },
                  },
                },
              }}
            />
          </div>
        )}
      </ChartCard>
    </div>
  );
}
